import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Loader2, Send, Share2, Trash2, ArrowLeft } from "lucide-react";
import {
  getIdea,
  listComments,
  createComment,
  deleteComment,
  toggleVote,
  isCurrentUserAdmin,
  adminSetStatus,
  adminAddUpdate,
  adminDeleteIdea,
  adminToggleFeatured,
  listMyVotes,
  LAB_STATUSES,
} from "@/lib/lab.functions";
import { StatusBadge, CategoryChip, statusLabel } from "@/components/lab/StatusBadge";
import { VoteButton } from "@/components/lab/VoteButton";
import { useCurrentUser } from "@/hooks/useCurrentUser";

export const Route = createFileRoute("/lab/$id")({ component: IdeaDetail });

function IdeaDetail() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useCurrentUser();
  const qc = useQueryClient();

  const getFn = useServerFn(getIdea);
  const commentsFn = useServerFn(listComments);
  const myVotesFn = useServerFn(listMyVotes);
  const voteFn = useServerFn(toggleVote);
  const createCmt = useServerFn(createComment);
  const deleteCmt = useServerFn(deleteComment);
  const adminFn = useServerFn(isCurrentUserAdmin);
  const setStatusFn = useServerFn(adminSetStatus);
  const addUpdateFn = useServerFn(adminAddUpdate);
  const deleteIdeaFn = useServerFn(adminDeleteIdea);
  const toggleFeaturedFn = useServerFn(adminToggleFeatured);

  const ideaQ = useQuery({ queryKey: ["lab-idea", id], queryFn: () => getFn({ data: { id } }) });
  const commentsQ = useQuery({ queryKey: ["lab-comments", id], queryFn: () => commentsFn({ data: { ideaId: id } }) });
  const votesQ = useQuery({
    queryKey: ["lab-my-votes"],
    queryFn: () => myVotesFn(),
    enabled: isAuthenticated,
    staleTime: 30_000,
  });
  const adminQ = useQuery({
    queryKey: ["lab-is-admin"],
    queryFn: () => adminFn(),
    enabled: isAuthenticated,
    staleTime: 5 * 60_000,
  });

  const voteMut = useMutation({
    mutationFn: () => voteFn({ data: { ideaId: id } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["lab-idea", id] });
      qc.invalidateQueries({ queryKey: ["lab-my-votes"] });
    },
  });

  const [commentBody, setCommentBody] = useState("");
  const cmtMut = useMutation({
    mutationFn: () => createCmt({ data: { ideaId: id, body: commentBody.trim() } }),
    onSuccess: () => {
      setCommentBody("");
      qc.invalidateQueries({ queryKey: ["lab-comments", id] });
      qc.invalidateQueries({ queryKey: ["lab-idea", id] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const delCmtMut = useMutation({
    mutationFn: (cid: string) => deleteCmt({ data: { id: cid } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["lab-comments", id] }),
  });

  const statusMut = useMutation({
    mutationFn: (status: any) => setStatusFn({ data: { id, status } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["lab-idea", id] });
      toast.success("Status atualizado");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const [note, setNote] = useState("");
  const noteMut = useMutation({
    mutationFn: () => addUpdateFn({ data: { ideaId: id, body: note.trim(), kind: "note" } }),
    onSuccess: () => {
      setNote("");
      qc.invalidateQueries({ queryKey: ["lab-idea", id] });
      toast.success("Atualização adicionada");
    },
  });

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ url, title: ideaQ.data?.idea.title });
      else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copiado");
      }
    } catch { /* cancelled */ }
  };

  if (ideaQ.isLoading) {
    return <div className="h-40 animate-pulse rounded-2xl bg-surface" />;
  }
  if (!ideaQ.data) {
    return <div className="rounded-2xl border border-[color:var(--hairline)] p-8 text-center text-sm text-muted-foreground">Ideia não encontrada.</div>;
  }
  const { idea, updates } = ideaQ.data;
  const voted = (votesQ.data ?? []).includes(idea.id);
  const isAdmin = !!adminQ.data?.isAdmin;

  return (
    <div>
      <button
        onClick={() => navigate({ to: "/lab" })}
        className="mb-4 inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Voltar
      </button>

      <div className="grid gap-6 lg:grid-cols-[1fr_280px]">
        <div>
          <div className="flex gap-4">
            <VoteButton
              size="lg"
              count={idea.vote_count}
              voted={voted}
              disabled={voteMut.isPending}
              onClick={() => {
                if (!isAuthenticated) { toast.info("Entre para votar"); return; }
                voteMut.mutate();
              }}
            />
            <div className="min-w-0 flex-1">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <StatusBadge status={idea.status} />
                <CategoryChip category={idea.category} />
              </div>
              <h1 className="font-display text-2xl font-bold tracking-tight lg:text-3xl">{idea.title}</h1>
              <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                <span>por <span className="font-medium text-foreground">@{idea.author?.username ?? "anônimo"}</span></span>
                <span>·</span>
                <time>{new Date(idea.created_at).toLocaleDateString("pt-BR")}</time>
                <button onClick={share} className="ml-auto inline-flex items-center gap-1 rounded-full border border-[color:var(--hairline)] bg-surface px-2.5 py-1 text-[11px] font-semibold text-foreground hover:border-primary/40">
                  <Share2 className="h-3 w-3" /> Compartilhar
                </button>
              </div>
              {idea.description && (
                <div className="mt-4 whitespace-pre-wrap rounded-2xl border border-[color:var(--hairline)] bg-surface p-4 text-sm leading-relaxed text-foreground">
                  {idea.description}
                </div>
              )}
            </div>
          </div>

          {/* Timeline */}
          <section className="mt-8">
            <h2 className="mb-3 text-sm font-bold uppercase tracking-wider text-muted-foreground">Atualizações</h2>
            {updates.length === 0 ? (
              <div className="rounded-xl border border-dashed border-[color:var(--hairline)] p-4 text-xs text-muted-foreground">
                Nenhuma atualização ainda.
              </div>
            ) : (
              <ol className="relative space-y-3 border-l border-[color:var(--hairline)] pl-4">
                {updates.map((u) => (
                  <li key={u.id} className="relative">
                    <span className="absolute -left-[19px] top-2 h-2 w-2 rounded-full bg-primary" />
                    <div className="rounded-xl border border-[color:var(--hairline)] bg-surface p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        {u.kind === "status_change" ? "Status" : u.kind === "milestone" ? "Marco" : "Nota"}
                        <span className="ml-2 font-normal normal-case">{new Date(u.created_at).toLocaleString("pt-BR")}</span>
                      </div>
                      <div className="mt-1 text-sm">{u.body}</div>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </section>

          {/* Comments */}
          <section className="mt-8">
            <h2 className="mb-3 text-sm font-bold uppercase tracking-wider text-muted-foreground">
              Comentários {idea.comment_count > 0 && `(${idea.comment_count})`}
            </h2>
            <div className="grid gap-2">
              {(commentsQ.data ?? []).map((c: any) => (
                <div key={c.id} className="rounded-xl border border-[color:var(--hairline)] bg-surface p-3">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span className="font-semibold text-foreground">@{c.author?.username ?? "anônimo"}</span>
                    <span>·</span>
                    <time>{new Date(c.created_at).toLocaleString("pt-BR")}</time>
                    {(user?.id === c.user_id || isAdmin) && (
                      <button
                        onClick={() => delCmtMut.mutate(c.id)}
                        className="ml-auto text-muted-foreground hover:text-destructive"
                        aria-label="Excluir"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                  <div className="mt-1 whitespace-pre-wrap text-sm">{c.body}</div>
                </div>
              ))}
              {(commentsQ.data ?? []).length === 0 && (
                <div className="rounded-xl border border-dashed border-[color:var(--hairline)] p-4 text-center text-xs text-muted-foreground">
                  Seja o primeiro a comentar.
                </div>
              )}
            </div>

            {isAuthenticated ? (
              <div className="mt-3 rounded-2xl border border-[color:var(--hairline)] bg-surface p-3">
                <textarea
                  value={commentBody}
                  onChange={(e) => setCommentBody(e.target.value.slice(0, 2000))}
                  rows={3}
                  placeholder="Adicione um comentário"
                  className="w-full resize-none rounded-xl border border-input bg-background p-2 text-sm focus:border-primary focus:outline-none"
                />
                <div className="mt-2 flex justify-end">
                  <button
                    onClick={() => cmtMut.mutate()}
                    disabled={cmtMut.isPending || commentBody.trim().length < 1}
                    className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-1.5 text-xs font-bold text-primary-foreground disabled:opacity-50"
                  >
                    {cmtMut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                    Comentar
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-3 rounded-xl border border-dashed border-[color:var(--hairline)] p-3 text-center text-xs text-muted-foreground">
                Entre para participar da conversa.
              </div>
            )}
          </section>
        </div>

        {/* Admin sidebar */}
        {isAdmin && (
          <aside className="h-fit rounded-2xl border border-primary/30 bg-primary/5 p-4">
            <div className="mb-3 text-xs font-bold uppercase tracking-wider text-primary">Admin</div>
            <div className="mb-3">
              <label className="mb-1 block text-[11px] font-semibold text-muted-foreground">Status</label>
              <select
                value={idea.status}
                onChange={(e) => statusMut.mutate(e.target.value)}
                className="w-full rounded-lg border border-input bg-background px-2 py-1.5 text-xs"
              >
                {LAB_STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
                <option value="archived">Arquivado</option>
              </select>
            </div>
            <div className="mb-3">
              <label className="mb-1 block text-[11px] font-semibold text-muted-foreground">Adicionar atualização</label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value.slice(0, 2000))}
                rows={3}
                placeholder="Nota para a comunidade"
                className="w-full resize-none rounded-lg border border-input bg-background p-2 text-xs"
              />
              <button
                onClick={() => noteMut.mutate()}
                disabled={noteMut.isPending || note.trim().length < 1}
                className="mt-2 w-full rounded-full bg-foreground px-3 py-1.5 text-[11px] font-bold text-background disabled:opacity-50"
              >
                Publicar atualização
              </button>
            </div>
            <button
              onClick={() => toggleFeaturedFn({ data: { id, featured: !idea.is_featured } }).then(() => qc.invalidateQueries({ queryKey: ["lab-idea", id] }))}
              className="mb-2 w-full rounded-full border border-[color:var(--hairline)] bg-background px-3 py-1.5 text-[11px] font-bold"
            >
              {idea.is_featured ? "Remover destaque" : "Destacar"}
            </button>
            <button
              onClick={() => {
                if (!confirm("Excluir esta ideia?")) return;
                deleteIdeaFn({ data: { id } }).then(() => navigate({ to: "/lab" }));
              }}
              className="w-full rounded-full border border-destructive/40 bg-destructive/10 px-3 py-1.5 text-[11px] font-bold text-destructive"
            >
              Excluir ideia
            </button>
            <div className="mt-3 text-[10px] text-muted-foreground">
              Status atual: <span className="font-semibold text-foreground">{statusLabel(idea.status)}</span>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}
